import type { Me } from '../lib/api';

/**
 * One line under the header saying where the account stands, when that is
 * something to act on: a trial running, a trial over, a failed charge, a
 * cancelled subscription. Nothing at all for a paying, healthy account -- a
 * banner that is always there is a banner nobody reads.
 *
 * Amber and red only for the last two, which are the ones where work is about
 * to stop or already has. A trial is information, not a warning.
 */
export function TrialBanner({ me, onBilling }: { me: Me; onBilling: () => void }) {
  const b = me.billing;
  if (!b || b.internal) return null;
  const isOwner = me.user?.role === 'owner';
  const cta = isOwner ? (
    <button type="button" className="banner-cta" onClick={onBilling}>
      {b.status === 'past_due' ? 'Update card' : b.status === 'trialing' ? 'Choose a plan' : 'Reactivate'}
    </button>
  ) : (
    <span className="banner-note">Ask an account owner to choose a plan.</span>
  );

  if (b.status === 'trialing') {
    if (b.billingScheduled) return null;
    const days = b.trialDaysLeft ?? 0;
    return (
      <div className="account-banner" data-tone={days <= 3 ? 'warn' : 'info'} role="status">
        <span>
          <strong>
            {days} {days === 1 ? 'day' : 'days'} left
          </strong>{' '}
          in your free trial of {b.planName}. Your setup carries over when you subscribe.
        </span>
        {cta}
      </div>
    );
  }

  if (b.status === 'trial_expired' || b.status === 'canceled' || b.status === 'unpaid') {
    return (
      <div className="account-banner" data-tone="late" role="alert">
        <span>
          <strong>{b.status === 'trial_expired' ? 'Your free trial has ended.' : 'This subscription is not active.'}</strong>{' '}
          You can still see everything, but changes are paused until a plan is chosen.
        </span>
        {cta}
      </div>
    );
  }

  if (b.status === 'past_due') {
    return (
      <div className="account-banner" data-tone="warn" role="alert">
        <span>
          <strong>The last payment failed.</strong> Routes keep running while it is retried — update the card to
          avoid interruption.
        </span>
        {cta}
      </div>
    );
  }

  return null;
}
